import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edge-functions';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Mail, Copy, Check } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

interface InviteMemberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leagueId: string;
  leagueName: string;
  onInviteSent?: () => void;
}

export function InviteMemberModal({
  open,
  onOpenChange,
  leagueId,
  leagueName,
  onInviteSent,
}: InviteMemberModalProps) {
  const { user } = useAuth();
  const { toast } = useToast();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [message, setMessage] = useState('');
  const [sendEmail, setSendEmail] = useState(true); // Default to sending email
  const [sending, setSending] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const resetForm = () => {
    setEmail('');
    setRole('member');
    setMessage('');
    setSendEmail(true);
    setInviteLink(null);
    setCopied(false);
    setEmailSent(false);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleSendInvite = async () => {
    if (!email.trim() || !user) {
      toast({
        title: 'Email required',
        description: 'Please enter an email address.',
        variant: 'destructive',
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    setSending(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();

      // Check if user exists in auth.users (via admin API would be better, but we'll check memberships)
      // Check if they're already a member of this league
      const { data: existingMembers } = await supabase
        .from('league_memberships')
        .select('user_id')
        .eq('league_id', leagueId);

      // If we have existing members, check if any match this email
      // Note: We can't directly query auth.users from client, so we'll rely on the invite system
      // The invite acceptance flow will handle checking if user already exists

      // Check if there are existing invites for this email
      const { data: existingInvites } = await supabase
        .from('league_invites')
        .select('id, expires_at, accepted_at')
        .eq('league_id', leagueId)
        .eq('email', normalizedEmail);

      if (existingInvites && existingInvites.length > 0) {
        // Check if any are pending and not expired — block duplicates
        const activePending = existingInvites.find(
          (inv) => !inv.accepted_at && new Date(inv.expires_at) >= new Date()
        );
        if (activePending) {
          toast({
            title: 'Invite already pending',
            description: 'There is already a pending invite for this email address.',
            variant: 'destructive',
          });
          setSending(false);
          return;
        }
        // Delete all old invites (expired or accepted) to make room for a new one
        const oldIds = existingInvites.map((inv) => inv.id);
        await supabase
          .from('league_invites')
          .delete()
          .in('id', oldIds);
      }

      // Create the invite
      const { data: invite, error } = await supabase
        .from('league_invites')
        .insert({
          league_id: leagueId,
          email: normalizedEmail,
          role: role,
          message: message.trim() || null,
          invited_by: user.id,
        })
        .select('id, invite_code')
        .single();

      if (error) {
        throw error;
      }

      // Generate invite link
      const link = `${window.location.origin}/invite/${invite.invite_code}`;
      setInviteLink(link);

      // Send email if requested
      if (sendEmail) {
        try {
          // Only send inviteId - all other data is fetched from the database
          // to prevent spoofing of email content
          const { data: result, error: emailError } = await invokeEdgeFunction('send-invite-email', {
            body: { inviteId: invite.id },
          });

          if (emailError) {
            console.warn('Failed to send invite email:', emailError);
            toast({
              title: 'Invite created',
              description: 'Email could not be sent. Please share the link manually.',
              variant: 'default',
            });
          } else {
            setEmailSent(true);
            toast({
              title: 'Invite sent!',
              description: `Invitation email sent to ${normalizedEmail}.`,
            });
          }
        } catch (emailError) {
          console.warn('Failed to send invite email:', emailError);
          toast({
            title: 'Invite created',
            description: 'Email could not be sent. Please share the link manually.',
            variant: 'default',
          });
        }
      } else {
        toast({
          title: 'Invite created!',
          description: 'Copy the link below to share with the invitee.',
        });
      }

      onInviteSent?.();
    } catch (err) {
      toast({
        title: 'Failed to create invite',
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = async () => {
    if (!inviteLink) return;

    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast({
        title: 'Link copied!',
        description: 'The invite link has been copied to your clipboard.',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: 'Failed to copy',
        description: 'Please copy the link manually.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite Member</DialogTitle>
          <DialogDescription>
            Send an invitation to join <strong>{leagueName}</strong>
          </DialogDescription>
        </DialogHeader>

        {inviteLink ? (
          // Success state - show the invite link
          <div className="space-y-4">
            {emailSent && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4 text-green-600" />
                  <p className="text-sm font-medium text-green-800">
                    Email sent successfully!
                  </p>
                </div>
                <p className="text-sm text-green-700">
                  An invitation email has been sent to <strong>{email}</strong>.
                </p>
              </div>
            )}
            <div className={`border rounded-lg p-4 ${emailSent ? 'bg-stone-50 border-stone-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-sm mb-3 ${emailSent ? 'text-stone-800' : 'text-green-800'}`}>
                {emailSent
                  ? 'You can also share this link manually:'
                  : `Invite created! Share this link with ${email}:`}
              </p>
              <div className="flex items-center gap-2">
                <Input
                  value={inviteLink}
                  readOnly
                  className="text-xs font-mono bg-white"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyLink}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <p className="text-xs text-stone-500">
              The invite will expire in 7 days. You can view pending invites in the Members tab.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={resetForm}>
                Send Another
              </Button>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          // Form state
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="member@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={sending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={role}
                onValueChange={(v) => setRole(v as 'member' | 'admin')}
                disabled={sending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-500">
                Admins can manage league settings and invite members.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Personal Message (optional)</Label>
              <Textarea
                id="message"
                placeholder="Hey! Join our fantasy football league..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={sending}
                rows={3}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="sendEmail"
                checked={sendEmail}
                onCheckedChange={(checked) => setSendEmail(checked === true)}
                disabled={sending}
              />
              <Label
                htmlFor="sendEmail"
                className="text-sm font-normal cursor-pointer"
              >
                Send invitation email automatically
              </Label>
            </div>
            <p className="text-xs text-stone-500">
              {sendEmail
                ? 'An email with the invitation link will be sent to the recipient.'
                : 'You can copy and share the invitation link manually.'}
            </p>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={sending}>
                Cancel
              </Button>
              <Button onClick={handleSendInvite} disabled={sending}>
                {sending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Create Invite
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

